import { AxiosRequestConfigMethods } from "@/api/types";
import { brandUrl, fetchFromBrandedUrl } from "@/api/utils";
import {
  EnvironmentVariables,
  SubDepartmentsDto,
} from "@/modules/common/types";
import {
  GetJobsResponse,
  GetRanksResponse,
  GetTagResponse,
  GetUserByIdV2Response,
  SubDepratmentParams,
} from "@/modules/user/types/index.v2";
import { BasicUserInfoFormData } from "../hooks/use-edit-user.hook";
import { NewHireTargetFormInputs } from "@/modules/targets/components/agents-targets/new-hire-target";
import { DepartmentsResponse } from "@/modules/user/types";

export const fetchTagsV2 = async (params: {}) => {
  const apiUrl = brandUrl<GetTagResponse>(
    `${EnvironmentVariables.UMS_API_URL}/tags`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params,
    },
  }) as Promise<GetTagResponse>;
};

export const fetchSubDepartmentsV2 = async (params: SubDepratmentParams) => {
  const apiUrl = brandUrl<SubDepartmentsDto>(
    `${EnvironmentVariables.UMS_API_URL}/sub-departments`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params,
      ...(!params.organizationUId && {
        includeSingleOrganization: true,
      }),
    },
  }) as Promise<SubDepartmentsDto>;
};

export const fetchRanksV2 = async (params: {}) => {
  const apiUrl = brandUrl<GetRanksResponse>(
    `${EnvironmentVariables.UMS_API_URL}/ranks`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params,
    },
  }) as Promise<GetRanksResponse>;
};

export const fetchJobsV2 = async (params: {}) => {
  const apiUrl = brandUrl<GetJobsResponse>(
    `${EnvironmentVariables.UMS_API_URL}/jobs`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params,
    },
  }) as Promise<GetJobsResponse>;
};

export const addNewUserV2 = async (data: any) => {
  const apiUrl = brandUrl<any>(`${EnvironmentVariables.UMS_API_URL_V2}/users`);

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data: { ...data },
    },
  });
};

export const fetchUserByUIdV2 = async (uuid: string) => {
  const apiUrl = brandUrl<GetUserByIdV2Response>(
    `${EnvironmentVariables.UMS_API_URL_V2}/users/${uuid}`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
    },
  }) as Promise<GetUserByIdV2Response>;
};

export const updateUserV2 = async (
  userId: string,
  data: Partial<
    BasicUserInfoFormData & { newHireTarget?: NewHireTargetFormInputs }
  >
) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.UMS_API_URL}/users/${userId}`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PUT,
      data: { ...data },
    },
  });
};

export const updateUserJobV2 = async (userId: string, jobId: number) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.UMS_API_URL}/users/${userId}/change-job`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PUT,
      data: { newJobId: jobId },
    },
  });
};

export const updateUserRankV2 = async (userId: string, rankUId: string) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.UMS_API_URL}/users/${userId}/rank`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PATCH,
      data: { rankUId: rankUId },
    },
  });
};

export const updateUserSupervisorV2 = async ({
  userId,
  supervisorUid,
  data,
  movementUnderLeaver,
}: {
  userId: string | undefined;
  supervisorUid: string | undefined;
  data?: {
    prorationDate: Date | null;
    targetsProration: {
      oldTarget: number;
      newTarget: number;
    }[];
  };
  movementUnderLeaver?: boolean;
}) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.UMS_API_URL_V2}/users/${userId}/supervisor/${supervisorUid}`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PUT,
      data: { ...data, movementUnderLeaver },
    },
  });
};

export const addTagToUserV2 = async (userId: string, tagId: string) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.UMS_API_URL}/users/${userId}/tag/${tagId}`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PUT,
    },
  });
};

export const removeTagFromUserV2 = async (userId: string, tagId: string) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.UMS_API_URL}/users/${userId}/tag/${tagId}`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.DELETE,
    },
  });
};

export const updateUserSubDepartmentV2 = async (
  userId: string,
  subDepartmentUId: string
) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.UMS_API_URL_V2}/users/${userId}/sub-department/${subDepartmentUId}`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PATCH,
    },
  });
};

export const updateUserStatusV2 = async (userUId: string, data: any) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.UMS_API_URL_V2}/users/${userUId}/change-status`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PUT,
      data: { ...data },
    },
  });
};

export const removeOrganizationMemberV2 = async ({
  userUId,
  prorationDate,
  periodValues,
}: {
  userUId: string;
  prorationDate?: Date;
  periodValues?: {
    from: Date;
    to: Date;
    amount: number;
  }[];
}) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.UMS_API_URL_V2}/users/${userUId}/leaver`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PUT,
      data: {
        prorationDate,
        periodValues,
      },
    },
  });
};

export const updateUserPools = async (userId: string, poolUIds: string[]) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/${userId}/user-pools`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PUT,
      data: { poolUIds },
    },
  });
};

export const getDepartments = async (params: {
  organizationUIds: string[];
  name?: string;
}) => {
  const apiUrl = brandUrl<DepartmentsResponse>(
    `${EnvironmentVariables.UMS_API_URL}/departments`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params,
    },
  }) as Promise<DepartmentsResponse>;
};
